import { CommercialInterestEditor } from ".";
import _ from "lodash";
import * as moment from "moment";
import * as React from "react";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { Paths } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    fd?: M.FinancialDisclosure.FD;
    loading?: boolean;
    user: M.User.BasicProfile;
    onSubmit: (model: Forms.FinancialDisclosure.FinancialDisclosureData, body: any) => void;
}

interface OwnState {
    itemIdxToToggle?: number;
}

export class FDUpdateForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.FinancialDisclosure.FinancialDisclosure();
        formModel.data.name = M.Generic.PersonHelper.getFullName(this.props.user);
        this.state = {
            ...this.getInitialState(formModel),
        };
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
        const FM = this.getFormModel() as Forms.FinancialDisclosure.FinancialDisclosure;
        const prevFM = (prevState as any).formModel as Forms.FinancialDisclosure.FinancialDisclosure;
        if (prevFM.data.commercialInterests.length === FM.data.commercialInterests.length - 1 && prevFM.data.commercialInterests.length >= 0) {
            // toggle newly added CI item for editing
            const lastItem = FM.data.commercialInterests[FM.data.commercialInterests.length - 1];
            this.setState({ ...this.state, itemIdxToToggle: lastItem.index });
        }
    }

    public setFormData(fd: M.FinancialDisclosure.FD) {
        const formModel = this.getFormModel() as Forms.FinancialDisclosure.FinancialDisclosure;
        formModel.data.name = fd.name;
        formModel.data.noCommercialInterests = fd.noCommercialInterests;
        formModel.data.commercialInterests = fd.commercialInterests.map((ci, i) => ({
            company: Utility.toReactSelectOption({}, ci.company || "", ci.company || ""),
            compensations: ci.compensationIds.map((x) => {
                const type = _.find(M.FinancialDisclosure.FDCompensationTypes.Values, (v) => v.id === x);
                return {
                    label: type ? `${type.name} (${type.code})` : x.toString(),
                    value: x,
                };
            }),
            id: ci.id,
            index: i,
            isDelete: false,
            isSpouse: ci.isSpouse,
            isVoid: false,
            relationships: ci.relationshipIds.map((x) => {
                const type = _.find(M.FinancialDisclosure.FDRelationshipTypes.Values, (v) => v.id === x);
                return {
                    label: type ? `${type.name} (${type.code})` : x.toString(),
                    value: x,
                };
            }),
        }));

        // if (!formModel.data.commercialInterests.length)
        //     formModel.data.commercialInterests.push({ ...Forms.FinancialDisclosure.DefaultCommercialInterest });

        if (formModel.data.noCommercialInterests)
            for (const ci of formModel.data.commercialInterests)
                ci.isVoid = true;

        this.updateFormModel(formModel);
    }

    public render() {

        const formModel = this.getFormModel() as Forms.FinancialDisclosure.FinancialDisclosure;
        const commercialInterestEditor = formModel.data.noCommercialInterests
            ? null
            : <Comp.Generic.WrapperLess>
                <CommercialInterestEditor
                    api={this.props.api}
                    formComponent={this}
                    itemIdxToToggle={this.state.itemIdxToToggle}
                    loading={this.props.loading}
                    namePrefix="commercialInterests"
                />
                <hr />
            </Comp.Generic.WrapperLess>;

        let lastUpdated: moment.Moment | undefined;
        if (this.props.fd && this.props.fd.dateLastUpdated)
            lastUpdated = moment(Utility.fromApiDate(this.props.fd.dateLastUpdated));

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onCustomValidation={() => this.validate()}
            onInputChange={(inputName) => this.handleInputChange(inputName)}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            <h2>Name</h2>

            <Comp.Editors.TextField formComponent={this} name="name" />

            {lastUpdated
                ? <Comp.Editors.ReviewField label="Last updated">{lastUpdated.format("MMMM D, YYYY")}</Comp.Editors.ReviewField>
                : null}

            <hr />

            <h2>Commercial Interests</h2>

            <Comp.Editors.CheckboxField
                formComponent={this}
                name="noCommercialInterests"
                label="I do not have any relevant financial relationships with any commercial interests"
            />

            {commercialInterestEditor}

            <Comp.Editors.CheckboxField
                formComponent={this}
                name="userAttests"
                label="I attest that the information provided above is accurate and complete"
                description={<p className="mute">
                    All the recommendations involving clinical medicine in a CME activity must be based on evidence that
                    is accepted within the profession of medicine as adequate justification for their indiciations and
                    contraindication in the care of patients. All scientific research referred to and reported or used
                    in CME in support or justification of a patient care recommendation must conform to the generally
                    accepted standards of experimental design, data collection, and analysis.
                </p>}
            />

            <Comp.Editors.SubmitField showProcessing={this.state.formProcessing} cancelLink={Paths.User.Account} />

        </Comp.Validation.ValidatedForm>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "noCommercialInterests") {
            const formModelData = this.getFormModelData<Forms.FinancialDisclosure.FinancialDisclosureData>();
            for (const ci of formModelData.commercialInterests)
                ci.isVoid = formModelData.noCommercialInterests;
            this.updateFormModelData(formModelData);
        }
    }

    private handleSubmit(model: Forms.FinancialDisclosure.FinancialDisclosureData) {
        const commercialInterests = model.noCommercialInterests
            ? []
            : model.commercialInterests
                .filter((ci) => !(ci.isDelete && ci.id === 0))
                .map((ci, i) => ({
                    companyName: ci.company ? ci.company.label : "",
                    compensationIds: ci.compensations.map((x) => x.value),
                    id: ci.id,
                    isDelete: ci.isDelete,
                    isSpouse: ci.isSpouse,
                    relationshipIds: ci.relationships.map((x) => x.value),
                }));

        const body = {
            commercialInterests,
            name: model.name,
            noCommercialInterests: model.noCommercialInterests,
            userAttests: model.userAttests,
        };

        this.props.onSubmit(model, body);
    }

    private validate() {
        const fieldErrors: M.Validation.IFieldValidationError[] = [];
        const model = this.getFormModelData<Forms.FinancialDisclosure.FinancialDisclosureData>();
        const ci = model.commercialInterests.filter((x) => !x.isDelete);
        if (!model.noCommercialInterests && !ci.length)
            fieldErrors.push(new M.Validation.FieldValidationError("commercialInterests", "Commercial Interests: Selection is required."));

        return fieldErrors;
    }
}



// WEBPACK FOOTER //
// ./src/containers/user/financialDisclosure/components/FDUpdateForm.tsx